export const claimDocumentation = `
## Overview

The Claims Edit/Create system provides comprehensive claim management functionality for healthcare billing and payment tracking. It supports complex business rules, automated status updates, and integrated CPT code management with ICD diagnosis codes.

## Form Fields Specification

### Basic Claim Information

| Field | Required | Editable | Type | Description |
|-------|----------|----------|------|-------------|
| Billing Status | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | Enum | Current billing status |
| Location | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | Service location |
| Appointment Provider | Conditional | <span class="checkmark">✓</span> | Object | Healthcare provider (auto-selected if only one available) |
| Insurance | Conditional | <span class="checkmark">✓</span> | Object | Insurance provider |
| ID |   | <span class="checkmark">✓</span> | String | Custom identifier field |
| Appointment Date | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | Date | Date of appointment |
| Document Date | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | Date | Document upload date |
| Patient Seen? | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | Boolean | Whether patient was seen |
| Document Uploaded | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | Boolean | Whether document was uploaded |
| Check Date |   | <span class="checkmark">✓</span> | Date | Check payment date |
| Check# |   | <span class="checkmark">✓</span> | String | Check number |
| Payment Method |   | <span class="checkmark">✓</span> | Enum | Payment method |
| Payment Date | Conditional | <span class="checkmark">✓</span> | Date | Payment processing date |
| Comments |   | <span class="checkmark">✓</span> | Text | Additional notes |
| Claim Status |   | <span class="crossmark">✗</span> | Enum | Auto-calculated claim status |

### Read-Only Audit Fields

| Field | Type | Description |
|-------|------|-------------|
| Created By | String | User who created the claim |
| Modified By | String | User who last modified the claim |
| Claim Generated Timestamp | DateTime | When claim was first generated |
| Last Modified Timestamp | DateTime | Last modification timestamp |

## Field Validation Rules

### Basic Field Validation

#### Billing Status Options
- \`billed\` - Claim has been billed
- \`pending\` - Awaiting processing
- \`icdIssue\` - ICD code issues
- \`insuranceIssue\` - Insurance-related issues
- \`appointmentCancelled\` - Appointment was cancelled
- \`readyToBill\` - Ready for billing
- \`appointmentTypeIssue\` - Issues with appointment type

#### Insurance Field Requirements
- **Required when**: Billing status is NOT \`insuranceIssue\` OR \`appointmentCancelled\`
- **Optional when**: Billing status is \`insuranceIssue\` OR \`appointmentCancelled\`

#### Patient Seen Auto-Rules
- When \`billingStatus === appointmentCancelled\` → \`isPatientSeen = false\`
- When \`billingStatus === billed\` → \`isPatientSeen = true\`

#### Date Validation
- **Appointment Date**: Defaults to today if not set
- **Document Date**: Defaults to appointmentDate or today
- **Payment Date**: Auto-sets to last day of month when Billing Status is \`billed\` with payments

#### Document Upload Auto-Rules
- When document date exists → \`isDocumentUploaded = true\`

### Conditional Requirements

#### Appointment Provider
- **Auto-selected**: When only one provider available for account/patient
- **Manual selection required**: When multiple providers available

#### Payment Date
- **Required when**: Any CPT activity has \`paid > 0\`
- **Auto-filled**: Last day of current month if billing status is \`billed\` and has payments
- **Cleared when**: CPT rows deleted or billing status changed to \`insuranceIssue\`/\`appointmentCancelled\`

## CPT Code Activities

### CPT Activity Fields

| Field | Required | Editable | Type | Validation | Description |
|-------|----------|----------|------|------------|-------------|
| CPT Code | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | From lookup API | Medical procedure code |
| Date of Service (DOS) | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | Date | ±30 days from appointment | Service date |
| Modifier |   | <span class="checkmark">✓</span> | String | From lookup API | CPT modifier code |
| ICD | <span class="checkmark">✓</span> | <span class="checkmark">✓</span> | String | From diagnosis codes API | Diagnosis code |
| Paid |   | <span class="checkmark">✓</span> | Number | 0-5000 range | Amount paid |
| PR (Patient Responsibility) |   | <span class="checkmark">✓</span> | Number | 0-5000 range | Patient responsibility amount |
| PR Type | Conditional | <span class="checkmark">✓</span> | Enum | Required when PR > 0 | Type of patient responsibility |
| Allowed |   | <span class="crossmark">✗</span> | Number | Auto-calculated | Allowed amount (PR + Paid) |
| Payment Date | Conditional | <span class="checkmark">✓</span> | Date | Required when paid > 0 | Payment processing date |
| CPT Status | Conditional | <span class="checkmark">✓</span> | Enum | Auto-set based on amounts | Current CPT status |

### CPT Activity Validation Rules

#### Date of Service Validation
- **Range**: Must be within ±30 days of appointment date
- **Auto-fill**: Defaults to appointment date or today

#### Financial Validation
- **Paid Amount**: 0-5000 range
- **PR Amount**: 0-5000 range
- **Allowed Amount**: Auto-calculated as \`paid + pr\` (read-only)

#### PR Type Requirements
- **Required when**: \`pr > 0\`
- **Options**: Copay, Coinsurance, Deductible

#### Payment Date Requirements
- **Required when**: \`paid > 0\`

#### CPT Status Auto-Assignment
- **When \`paid > 0\` and any PR**: Status = \`paid\`
- **When \`paid = 0\` and \`PR > 0\`**: Status = \`patientResponsibility\`

### Auto-CPT Generation Rules

#### Assessment-Based CPT Auto-Addition
When \`claim.entityKey === 'psychoEmotional'\` OR \`'pocHealth'\` AND \`claim.entityType === 'assessment'\`:

**Auto-adds CPT 96136:**
- Code: \`96136\`
- Modifier: \`59\`
- DOS: appointmentDate or today
- ICD: suggestedICD from lookup
- Amounts: All set to 0.00

**Auto-adds CPT 96137:**
- Code: \`96137\`
- DOS: appointmentDate or today
- ICD: suggestedICD from lookup
- Amounts: All set to 0.00

#### Manual CPT Trigger Rules

**When user adds CPT 96136:**
- Sets modifier to \`59\`
- Auto-adds CPT 96137 if not already present

**When user adds CPT 96137:**
- Sets DOS to appointmentDate or today

**When user adds CPT 99358:**
- Sets DOS to appointmentDate + 3 days

## Claim Status Management

### Status Auto-Calculation Logic

#### Based on Billing Status
1. **\`billingStatus === insuranceIssue\`** OR **\`icdIssue\`** → \`claimStatus = notBillable\`
2. **\`billingStatus === appointmentCancelled\`** → \`claimStatus = notBillable\`
3. **\`billingStatus === billed\`** → \`claimStatus = billed\` (if no CPT rows)
4. **\`billingStatus === pending\`** → \`claimStatus = pending\`

#### Based on CPT Activities
When CPT activities exist, claim status is determined by CPT statuses:

**All CPT Status Analysis:**
- **All CPT statuses = \`paid\`** → \`claimStatus = paid\`
- **All CPT statuses = \`denied\`** → \`claimStatus = denied\`
- **All CPT statuses = \`patientResponsibility\`** → \`claimStatus = patientResponsibility\`

**Payment Amount Analysis:**
- **All \`paid > 0\`** → \`claimStatus = paid\`
- **Some \`paid > 0\`, some = 0** → \`claimStatus = partiallyPaid\`
- **All \`paid = 0\` and all \`PR > 0\`** → \`claimStatus = patientResponsibility\`

### Claim Status Options
- \`billed\` - Claim has been billed
- \`patientResponsibility\` - Patient responsibility only
- \`inProcess\` - Currently being processed
- \`paid\` - Fully paid
- \`partiallyPaid\` - Partially paid
- \`denied\` - Claim denied
- \`resubmitted\` - Resubmitted for processing
- \`notBillable\` - Cannot be billed
- \`pending\` - Awaiting processing

## API Integration

### Primary Endpoints

#### Claim Management
- **GET** \`/accounts/{accountUuid}/claims/{claimUuid}\` - Get single claim
- **PATCH** \`/accounts/{accountUuid}/claims/{claimUuid}\` - Update claim

#### Lookup Data
- **GET** \`/accounts/{accountUuid}/patients/{patientUuid}/claims/lookup-info\` - Get form options
- **GET** \`/accounts/{accountUuid}/diagnosis-codes\` - Get ICD codes (with search)

### Lookup API Response Structure

\`\`\`json
{
  "providers": [...],
  "insurances": [...],
  "cptCodes": [...],
  "cptModifiers": [...],
  "entityKey": "psychoEmotional",
  "entityType": "assessment",
  "suggestedICD": "F43.10"
}
\`\`\`

### Claim Update Request Structure

\`\`\`json
{
  "billingStatus": "billed",
  "location": "Main Clinic",
  "appointmentProvider": { "uuid": "...", "name": "..." },
  "insurance": { "uuid": "...", "name": "..." },
  "appointmentDate": "2024-01-15",
  "documentDate": "2024-01-15",
  "isPatientSeen": true,
  "isDocumentUploaded": true,
  "paymentMethod": "Check",
  "notes": "Additional comments",
  "activities": [
    {
      "code": "96136",
      "dos": "2024-01-15",
      "modifier": "59",
      "icd": "F43.10",
      "paid": 150.00,
      "pr": 25.00,
      "prType": "Copay",
      "paymentDate": "2024-01-31",
      "cptStatus": "paid"
    }
  ]
}
\`\`\`

## Permission Requirements

### Required Permissions
- **CLAIM_GET** (BigInt 39) - View individual claim details
- **CLAIM_PATCH** (BigInt 40) - Edit and update claims
- **CLAIMS_GET** (BigInt 41) - Access claims functionality
- **SURVEYS_GET** (BigInt 22) - Download associated program files

### Permission Context
- **Account-Level Access** - Users can only edit claims for their associated accounts
- **Distribution Sponsor Access** - Enhanced permissions for managing multiple account claims

## UI Functionality

### Form Behavior

#### Auto-Fill Functionality
1. **Provider Selection** - Auto-selects when only one provider available
2. **Date Defaults** - Appointment and document dates default to today
3. **Status Updates** - Claim status auto-updates based on billing status and CPT activities
4. **Payment Dates** - Auto-fills last day of month for billed claims with payments
5. **CPT Auto-Addition** - Assessment-based automatic CPT code generation

#### Form Validation
- **Real-time validation** on field changes
- **Date range enforcement** for DOS fields
- **Conditional field requirements** based on status and amounts
- **Business rule validation** for status combinations

#### User Interactions
- **Add/Remove CPT Activities** - Dynamic CPT activity management
- **ICD Code Search** - Dynamic search for diagnosis codes
- **Status Dependencies** - UI updates based on status selections
- **Auto-Save Functionality** - Saves changes automatically

### CPT Activity Management

#### CPT Code Selection
- **Lookup Integration** - Codes loaded from account-specific settings
- **Auto-Complete** - Search functionality for CPT codes
- **Validation** - Ensures codes are valid for account

#### ICD Code Selection
- **Dynamic Search** - Real-time search through diagnosis codes API
- **Auto-Complete** - Search suggestions as user types
- **Validation** - Ensures valid ICD codes

#### Row Management
- **Add Rows** - Dynamic addition of CPT activities
- **Remove Rows** - Delete CPT activities with confirmation
- **Row Validation** - Individual row validation
- **Auto-Fill** - Special code rules trigger auto-population

### Error Handling

#### Validation Errors
- **Field-Level Errors** - Individual field validation messages
- **Form-Level Errors** - Overall form validation summary
- **Business Rule Violations** - Status and logic conflict messages

#### API Errors
- **Network Errors** - Connection failure handling
- **Server Errors** - Backend validation error display
- **Permission Errors** - Access denied messaging

## Testing

For comprehensive testing scenarios and interactive checklists for claims functionality, please refer to the **Testing Scenarios** section in the documentation navigation.

## Business Rules Summary

### Critical Business Logic
1. **Status Dependencies** - Billing status affects claim status and field requirements
2. **Auto-Status Updates** - System automatically updates statuses based on activities
3. **Conditional Requirements** - Field requirements change based on other field values
4. **Financial Validation** - Amount limits and payment date requirements
5. **Date Constraints** - DOS must be within appointment date range
6. **Assessment Integration** - Special CPT rules for assessment-based claims
7. **Audit Trail** - All changes tracked with user and timestamp information

### Key Integration Points
- **Patient Management** - Claims linked to patient records
- **Account Configuration** - Account-specific CPT code availability
- **Assessment System** - Assessment completion triggers claim creation
- **Billing System** - Claims feed billing and payment processes
- **Reporting** - Claims data feeds financial and operational reports
`;
